import { ReactNode, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import SawLogo from '../../assets/img/rewamp/saw-logo.svg';
import '../../styles/form-page.scss';

const TRUSTPILOT_SCRIPT = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

const useTrustpilot = () => {
  const widget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = widget.current;
    if (!element) return;

    const isPhone = window.matchMedia('(max-width: 639px)').matches;
    element.dataset.styleWidth = isPhone ? '80px' : '100%';
    element.dataset.styleHeight = isPhone ? '52px' : '20px';

    const trustpilot = (window as any).Trustpilot;
    if (trustpilot) {
      trustpilot.loadFromElement(element);
    } else if (!document.querySelector(`script[src="${TRUSTPILOT_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.src = TRUSTPILOT_SCRIPT;
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  return widget;
};

interface FormPageLayoutProps {
  title: string;
  heading: string;
  subtitle: string;
  intro?: ReactNode;
  children: ReactNode;
}

export const FormPageLayout: React.FC<FormPageLayoutProps> = ({ title, heading, subtitle, intro, children }): JSX.Element => {
  const trustpilot = useTrustpilot();

  return (
    <div className="form-page">
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className="form-page-inner">
        <div className="form-page-header">
          <a href="https://saw.com" aria-label="Saw.com">
            <img className="form-page-logo" src={SawLogo} alt="Saw.com" />
          </a>
          <div
            ref={trustpilot}
            className="trustpilot-widget"
            data-locale="en-US"
            data-template-id="5419b637fa0340045cd0c936"
            data-businessunit-id="5e4179e55dd2990001312959"
            data-token="ad239576-fd4f-43cb-95a5-e2a0f354dda0"
          >
            <a href="https://www.trustpilot.com/review/saw.com" target="_blank" rel="noopener noreferrer">Trustpilot</a>
          </div>
        </div>
        <div className="form-page-body">
          <div>
            <h1 className="form-page-heading">{heading}</h1>
            <p className="form-page-subtitle">{subtitle}</p>
            {intro}
          </div>
          {children}
        </div>
        <p className="text-center fs-16 fw-500 mt-5 mb-0">
          Need help? Contact <a href="mailto:support@saw.com">support@saw.com</a>
        </p>
      </div>
    </div>
  );
};

export default FormPageLayout;
